import { Injectable } from '@angular/core';
import { HealthcareService, CodeableConcept, Reference, ContactPoint, Period, Extension } from 'fhir/r4';
import { FhirResourceConversionBaseService, ResourceContextOverrides } from '../shared/resource-conversion-base.service';
import { UtilityService } from '../shared/utility.service';
import { StateService } from '../shared/state.service';

@Injectable({ providedIn: 'root' })
export class HealthcareServiceConversionService extends FhirResourceConversionBaseService<HealthcareService> {
  private readonly practitionerAuthorExtensionUrl = 'http://elikita.org/fhir/StructureDefinition/resource-author';
  private readonly patientContextExtensionUrl = 'http://elikita.org/fhir/StructureDefinition/context-patient';
  private readonly encounterContextExtensionUrl = 'http://elikita.org/fhir/StructureDefinition/context-encounter';

  constructor(
    utility: UtilityService,
    stateService: StateService
  ) {
    super(utility, stateService);
  }

  convertFhirResourceToDynamicFormFormat(resource: HealthcareService | null | undefined): any {
    if (!resource) {
      return {
        active: 'active'
      };
    }

    return {
      name: resource.name ?? '',
      active: resource.active === false ? 'inactive' : 'active',
      category: this.toDelimitedArray(resource.category),
      type: this.toDelimitedArray(resource.type),
      specialty: this.toDelimitedArray(resource.specialty),
      program: this.ensureArray(resource.program).map(program =>
        this.utility.delimitedFromCodeableConcept(program) ?? program?.text ?? ''
      ).filter(Boolean),
      characteristic: this.toDelimitedArray(resource.characteristic),
      providedBy: this.utility.delimitedFromReference(resource.providedBy) ?? '',
      locations: this.ensureArray(resource.location)
        .map(ref => this.utility.delimitedFromReference(ref) ?? '')
        .filter(Boolean),
      coverageArea: this.ensureArray(resource.coverageArea)
        .map(ref => this.utility.delimitedFromReference(ref) ?? '')
        .filter(Boolean),
      telecom: this.ensureArray(resource.telecom).map(point => ({
        system: point.system ?? '',
        use: point.use ?? '',
        value: point.value ?? ''
      })),
      availability: this.ensureArray(resource.availableTime).map(time => ({
        daysOfWeek: this.ensureArray(time.daysOfWeek),
        allDay: !!time.allDay,
        availableStartTime: this.parseTimeString(time.availableStartTime),
        availableEndTime: this.parseTimeString(time.availableEndTime)
      })),
      notAvailable: this.ensureArray(resource.notAvailable).map(entry => ({
        description: entry.description ?? '',
        duringStart: entry.during?.start ? this.formatDateTimeLocal(entry.during.start) : '',
        duringEnd: entry.during?.end ? this.formatDateTimeLocal(entry.during.end) : ''
      })),
      comment: resource.comment ?? '',
      extraDetails: resource.extraDetails ?? ''
    };
  }

  convertDynamicFormFormatToFhirResource(
    dynamicFormData: any,
    existing?: HealthcareService | null
  ): HealthcareService {
    const base: HealthcareService = {
      ...(existing ?? {}),
      resourceType: 'HealthcareService'
    };

    base.name = (dynamicFormData?.name ?? '').trim() || existing?.name;
    base.active = this.mapActiveFlag(dynamicFormData?.active, existing?.active);

    this.assignCodeableConceptArray(base, 'category', dynamicFormData?.category ?? existing?.category);
    this.assignCodeableConceptArray(base, 'type', dynamicFormData?.type ?? existing?.type);
    this.assignCodeableConceptArray(base, 'specialty', dynamicFormData?.specialty ?? existing?.specialty);
    this.assignCodeableConceptArray(base, 'program', dynamicFormData?.program ?? existing?.program, true);
    this.assignCodeableConceptArray(base, 'characteristic', dynamicFormData?.characteristic ?? existing?.characteristic, true);

    const locationRefs = this.buildReferenceArray(dynamicFormData?.locations ?? existing?.location);
    if (locationRefs?.length) {
      base.location = locationRefs as Reference[];
    } else {
      delete base.location;
    }

    const coverageRefs = this.buildReferenceArray(dynamicFormData?.coverageArea ?? existing?.coverageArea);
    if (coverageRefs?.length) {
      base.coverageArea = coverageRefs as Reference[];
    } else {
      delete base.coverageArea;
    }

    const telecom = this.buildTelecomArray(dynamicFormData?.telecom ?? existing?.telecom);
    if (telecom?.length) {
      base.telecom = telecom;
    } else {
      delete base.telecom;
    }

    const availability = this.buildAvailability(dynamicFormData?.availability ?? existing?.availableTime);
    if (availability?.length) {
      base.availableTime = availability as HealthcareService['availableTime'];
    } else {
      delete base.availableTime;
    }

    const notAvailable = this.buildNotAvailable(dynamicFormData?.notAvailable ?? existing?.notAvailable);
    if (notAvailable?.length) {
      base.notAvailable = notAvailable as HealthcareService['notAvailable'];
    } else {
      delete base.notAvailable;
    }

    const comment = (dynamicFormData?.comment ?? '').trim();
    if (comment) {
      base.comment = comment;
    } else {
      delete base.comment;
    }

    const extraDetails = (dynamicFormData?.extraDetails ?? '').trim();
    if (extraDetails) {
      base.extraDetails = extraDetails;
    } else {
      delete base.extraDetails;
    }

    return base;
  }

  eliminateEmptyFieldsInShallowAndDeepPaths(resource: HealthcareService): HealthcareService {
    if (resource.telecom) {
      resource.telecom = this.ensureArray(resource.telecom)
        .filter(point => !!point?.value)
        .map(point => ({
          system: point.system,
          use: point.use,
          value: point.value?.trim()
        })) as ContactPoint[];
      if (!resource.telecom.length) {
        delete resource.telecom;
      }
    }

    const availableTime = this.ensureArray<NonNullable<HealthcareService['availableTime']>[number]>(
      resource.availableTime as HealthcareService['availableTime']
    )
      .filter(slot =>
        this.ensureArray(slot.daysOfWeek).length || slot.availableStartTime || slot.availableEndTime || slot.allDay === true
      ) as HealthcareService['availableTime'];
    if (availableTime?.length) {
      resource.availableTime = availableTime;
    } else {
      delete resource.availableTime;
    }

    const notAvailable = this.ensureArray<NonNullable<HealthcareService['notAvailable']>[number]>(
      resource.notAvailable as HealthcareService['notAvailable']
    )
      .filter(entry => !!entry.description)
      .map(entry => ({
        description: entry.description,
        during: entry.during?.start || entry.during?.end ? entry.during : undefined
      })) as HealthcareService['notAvailable'];
    if (notAvailable?.length) {
      resource.notAvailable = notAvailable;
    } else {
      delete resource.notAvailable;
    }

    resource.location = this.ensureArray(resource.location).filter(ref => !!ref?.reference);
    if (!resource.location.length) {
      delete resource.location;
    }

    resource.coverageArea = this.ensureArray(resource.coverageArea).filter(ref => !!ref?.reference);
    if (!resource.coverageArea.length) {
      delete resource.coverageArea;
    }

    const cleaned = this.pruneEmpty(resource) || resource;
    const finalized: HealthcareService = {
      ...cleaned,
      resourceType: 'HealthcareService'
    };
    if (finalized.active === undefined || finalized.active === null) {
      finalized.active = true;
    }
    return finalized;
  }

  attachAppStateContext(resource: HealthcareService, overrides?: ResourceContextOverrides): HealthcareService {
    const ctx = this.buildContextReferences(overrides);
    const extensions: Extension[] = [...this.ensureArray(resource.extension)];

    if (ctx.practitioner) {
      this.upsertContextExtension(extensions, this.practitionerAuthorExtensionUrl, ctx.practitioner);
    }
    if (ctx.patient) {
      this.upsertContextExtension(extensions, this.patientContextExtensionUrl, ctx.patient);
    }
    if (ctx.encounter) {
      this.upsertContextExtension(extensions, this.encounterContextExtensionUrl, ctx.encounter);
    }

    if (extensions.length) {
      resource.extension = extensions;
    }

    resource.meta = {
      ...(resource.meta ?? {}),
      lastUpdated: new Date().toISOString()
    };

    return resource;
  }

  private mapActiveFlag(value: any, fallback?: boolean): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase() !== 'inactive';
    }
    if (typeof value === 'boolean') {
      return value;
    }
    return fallback ?? true;
  }

  private formatDateTimeLocal(isoString?: string): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `--T:`;
  }

  private toDelimitedArray(concepts?: CodeableConcept[] | null): string[] {
    return this.ensureArray(concepts)
      .map(concept => this.utility.delimitedFromCodeableConcept(concept) ?? concept?.text ?? '')
      .filter(Boolean);
  }

  private assignCodeableConceptArray(
    target: HealthcareService,
    key: keyof Pick<HealthcareService, 'category' | 'type' | 'specialty' | 'program' | 'characteristic'>,
    rawValue: any,
    textOnly = false
  ): void {
    const values = this.ensureArray(rawValue)
      .map(entry => {
        if (!entry) {
          return null;
        }
        if (typeof entry === 'string') {
          if (textOnly && !entry.includes('$#$')) {
            return { text: entry } as CodeableConcept;
          }
          return this.utility.codeableConceptFromDelimited(entry);
        }
        if ((entry as CodeableConcept)?.coding || (entry as CodeableConcept)?.text) {
          return entry as CodeableConcept;
        }
        return null;
      })
      .filter((concept): concept is CodeableConcept => !!concept);

    if (values.length) {
      (target as any)[key] = values;
    } else {
      delete (target as any)[key];
    }
  }

  private buildReferenceArray(raw: any): Reference[] | undefined {
    const values = this.ensureArray(raw)
      .map(item => this.utility.referenceFromDelimited(item ?? ''))
      .filter((ref): ref is Reference => !!ref?.reference);
    return values.length ? values : undefined;
  }

  private buildPeriod(start?: string, end?: string): Period | undefined {
    if (!start && !end) {
      return undefined;
    }
    return {
      start: start || undefined,
      end: end || undefined
    };
  }

  private parseTimeString(timeStr?: string): Date | undefined {
    if (!timeStr) return undefined;
    const parts = timeStr.split(':').map(Number);
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return undefined;
    const date = new Date();
    date.setHours(parts[0], parts[1], 0, 0);
    return date;
  }

  private formatTimeDate(date?: Date | string | null): string | undefined {
    if (!date) return undefined;
    if (typeof date === 'string') return date;
    if (!(date instanceof Date) || isNaN(date.getTime())) return undefined;
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    const s = date.getSeconds().toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  private buildTelecomArray(raw: any): ContactPoint[] | undefined {
    const telecom = this.ensureArray(raw)
      .map((entry: any) => ({
        system: entry?.system || undefined,
        use: entry?.use || undefined,
        value: entry?.value?.trim() || undefined
      }))
      .filter(point => !!point.value);
    return telecom.length ? telecom as ContactPoint[] : undefined;
  }

  private buildAvailability(raw: any): HealthcareService['availableTime'] | undefined {
    const availability = this.ensureArray(raw)
      .map((entry: any) => ({
        daysOfWeek: this.ensureArray(entry?.daysOfWeek).filter(Boolean),
        allDay: entry?.allDay === true,
        availableStartTime: entry?.allDay ? undefined : (this.formatTimeDate(entry?.availableStartTime) || undefined),
        availableEndTime: entry?.allDay ? undefined : (this.formatTimeDate(entry?.availableEndTime) || undefined)
      }))
      .filter(slot => slot.daysOfWeek.length || slot.availableStartTime || slot.availableEndTime || slot.allDay);
    return availability.length ? availability as HealthcareService['availableTime'] : undefined;
  }

  private buildNotAvailable(raw: any): HealthcareService['notAvailable'] | undefined {
    const entries: NonNullable<HealthcareService['notAvailable']> = [];

    for (const entry of this.ensureArray(raw)) {
      const description = (entry?.description ?? '').trim();
      if (!description) {
        continue;
      }

      const rawStart = entry?.duringStart ?? entry?.during?.start ?? undefined;
      const rawEnd = entry?.duringEnd ?? entry?.during?.end ?? undefined;

      const start = rawStart ? new Date(rawStart).toISOString() : undefined;
      const end = rawEnd ? new Date(rawEnd).toISOString() : undefined;

      const notAvailableEntry: NonNullable<HealthcareService['notAvailable']>[number] = {
        description,
        ...(start || end ? { during: { start, end } as Period } : {})
      };

      entries.push(notAvailableEntry);
    }

    return entries.length ? entries : undefined;
  }

  private upsertContextExtension(extensions: Extension[], url: string, valueReference?: Reference): void {
    if (!valueReference) {
      return;
    }
    const idx = extensions.findIndex(ext => ext.url === url);
    if (idx > -1) {
      extensions[idx] = { url, valueReference };
    } else {
      extensions.push({ url, valueReference });
    }
  }
}


